import * as React from "react";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ExpenseLineItem, OtherBillFieldValues, OtherClaimType } from "../../types";
import { formatDate } from "@/lib/datetime";

/** Required per claim type - kept in step with requests_api.py's
 *  _OTHER_BILL_REQUIRED (the server enforces the same list). */
const REQUIRED: Record<OtherClaimType, (keyof OtherBillFieldValues)[]> = {
  city_travel: ["startPlace", "endPlace", "purpose", "buyer"],
  miscellaneous: ["buyer"]
};

const LABEL: Record<keyof OtherBillFieldValues, string> = {
  startPlace: "start place",
  endPlace: "end place",
  purpose: "purpose",
  modeOfTransport: "mode of transport",
  buyer: "buyer"
};

/** The first required field left blank, as a readable name ("start
 *  place"), or null when the bill's Other fields are complete. */
export function missingOtherBillField(
  claimType: OtherClaimType,
  values: OtherBillFieldValues
): string | null {
  const key = REQUIRED[claimType].find((k) => !values[k]?.trim());
  return key ? LABEL[key] : null;
}

/** An existing bill's Other fields, for seeding an edit form. */
export function otherBillValuesOf(expense: ExpenseLineItem): OtherBillFieldValues {
  return {
    startPlace: expense.startPlace ?? "",
    endPlace: expense.endPlace ?? "",
    purpose: expense.purpose ?? "",
    modeOfTransport: expense.modeOfTransport ?? "",
    buyer: expense.buyer ?? ""
  };
}

/** Only the keys a claim type uses, trimmed - what the upload/edit routes
 *  are sent. */
export function otherBillPayload(
  claimType: OtherClaimType,
  values: OtherBillFieldValues
): OtherBillFieldValues {
  const keys: (keyof OtherBillFieldValues)[] =
    claimType === "city_travel"
      ? ["startPlace", "endPlace", "purpose", "modeOfTransport", "buyer"]
      : ["buyer"];
  return Object.fromEntries(keys.map((k) => [k, (values[k] ?? "").trim()]));
}

export function otherBillValuesEqual(a: OtherBillFieldValues, b: OtherBillFieldValues): boolean {
  return (Object.keys(LABEL) as (keyof OtherBillFieldValues)[]).every(
    (k) => (a[k] ?? "") === (b[k] ?? "")
  );
}

/** One line summarising a saved Other bill's own fields, for bill lists -
 *  "Office → Airport · Client pickup · Cab · Buyer: Ranjith". */
export function otherBillSummary(expense: ExpenseLineItem): string {
  const parts: string[] = [];
  if (expense.startPlace || expense.endPlace) {
    parts.push(`${expense.startPlace || "-"} → ${expense.endPlace || "-"}`);
  }
  if (expense.purpose) parts.push(expense.purpose);
  if (expense.modeOfTransport) parts.push(expense.modeOfTransport);
  if (expense.buyer) parts.push(`Buyer: ${expense.buyer}`);
  return parts.join(" · ");
}

/** Every field on a saved Other bill, as (label, value) pairs for a popover/
 *  detail view - the shared fields every bill has (Date, Category, Amount)
 *  followed by the claim-type-specific ones (see otherBillSummary's own doc
 *  comment for the packed one-line form of just the latter, still used in
 *  tighter spaces like ExpensesStep's pending-bill cards). */
export function otherBillDetails(expense: ExpenseLineItem): { label: string; value: string }[] {
  const out: { label: string; value: string }[] = [];
  if (expense.date) out.push({ label: "Date", value: formatDate(expense.date) });
  out.push({ label: "Category", value: expense.category });
  if (expense.startPlace || expense.endPlace) {
    out.push({ label: "Route", value: `${expense.startPlace || "-"} → ${expense.endPlace || "-"}` });
  }
  if (expense.purpose) out.push({ label: "Purpose", value: expense.purpose });
  if (expense.modeOfTransport) out.push({ label: "Mode of transport", value: expense.modeOfTransport });
  if (expense.buyer) out.push({ label: "Buyer", value: expense.buyer });
  // Miscellaneous's own optional field - City travel has its own Purpose
  // instead (see OtherBillInputs).
  if (expense.description) out.push({ label: "Description", value: expense.description });
  out.push({ label: "Amount", value: `${expense.currency} ${expense.amount.toLocaleString()}` });
  return out;
}

/** The Other reimbursement bill inputs (City travel: start/end place,
 *  purpose, mode of transport, buyer; Miscellaneous: buyer), laid out as
 *  cells of the caller's two-column grid. Category, amount, date and (for
 *  Miscellaneous) description stay the caller's own fields. */
export function OtherBillInputs({
  claimType,
  value,
  onChange,
  idPrefix
}: {
  claimType: OtherClaimType;
  value: OtherBillFieldValues;
  onChange: (patch: OtherBillFieldValues) => void;
  idPrefix: string;
}) {
  const field = (
    key: keyof OtherBillFieldValues,
    label: string,
    placeholder: string,
    optional = false
  ) => (
    <div className="space-y-1.5">
      <Label htmlFor={`${idPrefix}-${key}`}>
        {label}
        {optional && <span className="font-normal text-muted-foreground"> (optional)</span>}
      </Label>
      <Input
        id={`${idPrefix}-${key}`}
        value={value[key] ?? ""}
        onChange={(e) => onChange({ [key]: e.target.value })}
        placeholder={placeholder}
        maxLength={200}
      />
    </div>
  );

  if (claimType === "miscellaneous") {
    return field("buyer", "Buyer", "Who made this purchase");
  }
  return (
    <>
      {field("startPlace", "Start place", "Enter city name")}
      {field("endPlace", "End place", "Enter city name")}
      {field("purpose", "Purpose", "e.g. Client meeting")}
      {field("modeOfTransport", "Mode of transport", "e.g. Cab, Auto, Metro", true)}
      {field("buyer", "Buyer", "Enter Client name or your name")}
    </>
  );
}
